import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanLine, Camera, Loader2, Check, X } from "lucide-react";

/**
 * VinScanner
 *
 * Props:
 *  - value: string (current VIN)
 *  - onChange: (vin) => void  — updates VIN as user types
 *  - onDecoded: (decoded) => void  — called when NHTSA decode succeeds
 *  - autoDecode: bool (default true)  — auto-call NHTSA when 17 chars
 *  - showResult: bool (default true) — show decoded make/model preview
 */
export default function VinScanner({ value = "", onChange, onDecoded, autoDecode = true, showResult = true }) {
  const [decoded, setDecoded] = useState(null);
  const [decoding, setDecoding] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!autoDecode) return;
    const v = (value || "").trim().toUpperCase();
    if (v.length !== 17) { setDecoded(null); setError(""); return; }
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(v)) { setError("VIN inválido (não use I, O, Q)"); return; }
    const t = setTimeout(() => decodeNow(v), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [value]);

  const decodeNow = async (v) => {
    setError("");
    setDecoding(true);
    try {
      const { data } = await api.get(`/vin/decode/${v}`);
      setDecoded(data);
      onDecoded?.(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "VIN não encontrado";
      setError(msg);
      setDecoded(null);
    } finally { setDecoding(false); }
  };

  const scanImage = async (file) => {
    if (!file) return;
    setScanning(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vin/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const vin = data.vin;
      onChange?.(vin);
      toast.success(`VIN identificado: ${vin}`);
      // decode will trigger via useEffect
    } catch (e) {
      const msg = e?.response?.data?.detail || "Não foi possível ler o VIN";
      toast.error(msg);
      setError(msg);
    } finally { setScanning(false); }
  };

  return (
    <div className="space-y-2" data-testid="vin-scanner">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={value}
            onChange={(e) => onChange?.(e.target.value.toUpperCase().replace(/\s/g, ""))}
            placeholder="Digite ou escaneie o VIN (17 chars)"
            className="pl-9 font-mono uppercase tracking-wider"
            maxLength={17}
            spellCheck={false}
            autoCapitalize="off"
            data-testid="vin-input"
          />
          {decoding && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />}
          {!decoding && decoded && <Check className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" />}
        </div>
        <label
          className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-input bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer text-sm font-medium"
          data-testid="vin-scan-btn"
        >
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          <span className="hidden md:inline">{scanning ? "Lendo…" : "Escanear"}</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { scanImage(e.target.files?.[0]); e.target.value = ""; }}
            data-testid="vin-scan-input"
          />
        </label>
      </div>
      {error && (
        <div className="text-[11px] text-red-500 flex items-center gap-1">
          <X className="w-3 h-3" />{error}
        </div>
      )}
      {showResult && decoded && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-md p-3 text-sm" data-testid="vin-decoded">
          <div className="flex items-center gap-2 mb-1">
            <Check className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-[10px] uppercase tracking-widest text-emerald-500">Reconhecido via NHTSA</span>
          </div>
          <div className="font-display font-semibold">
            {decoded.year} {decoded.make} {decoded.model} {decoded.trim && <span className="text-muted-foreground font-normal">{decoded.trim}</span>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {[decoded.engine, decoded.fuel, decoded.drivetrain, decoded.body_class].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}
    </div>
  );
}
